import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, Sparkles, FileText, Plus } from 'lucide-react';
import {
    TreatmentNoteTemplate, SupportedLocale, TemplateCategory,
} from '../../../services/apiService';
import { pickLocale } from '../../../utils/i18n';

const CATEGORY_LABEL: Record<TemplateCategory, string> = {
    prophylaxis: 'Prophylaxe',
    restorative: 'Konservierend',
    endodontics: 'Endodontie',
    implant: 'Implantologie',
    aesthetics: 'Ästhetik',
    consultation: 'Beratung',
    emergency: 'Notfall',
    diagnostic: 'Diagnostik',
    other: 'Sonstiges',
};

const CATEGORY_ORDER: TemplateCategory[] = [
    'prophylaxis', 'restorative', 'endodontics', 'implant',
    'aesthetics', 'consultation', 'emergency', 'diagnostic', 'other',
];

interface Props {
    open: boolean;
    templates: TreatmentNoteTemplate[];
    practitionerName: string;
    patientName: string;
    locale: SupportedLocale;
    onSelect: (template: TreatmentNoteTemplate) => void;
    onSelectFreetext: () => void;
    onClose: () => void;
}

const NewTreatmentNoteDialog: React.FC<Props> = ({
    open, templates, practitionerName, patientName, locale,
    onSelect, onSelectFreetext, onClose,
}) => {
    const [query, setQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState<TemplateCategory | 'all'>('all');
    const [aiAssist, setAiAssist] = useState(false);

    useEffect(() => {
        if (!open) {
            setQuery('');
            setActiveCategory('all');
            setAiAssist(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return templates.filter(t => {
            if (activeCategory !== 'all' && t.category !== activeCategory) return false;
            if (!q) return true;
            const name = pickLocale(t.name, locale).toLowerCase();
            return name.includes(q) || t.slug.toLowerCase().includes(q);
        });
    }, [templates, query, activeCategory, locale]);

    const presentCategories = useMemo(() => {
        const set = new Set(templates.map(t => t.category));
        return CATEGORY_ORDER.filter(c => set.has(c));
    }, [templates]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal="true" aria-label="Neue Dokumentation">
            <div className="absolute inset-0" onClick={onClose} aria-hidden />
            <div className="relative w-full max-w-2xl max-h-[80vh] flex flex-col bg-brand-surface rounded-xl border border-brand-border shadow-xl overflow-hidden">
                {/* Header */}
                <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-brand-border">
                    <div>
                        <h2 className="text-base font-semibold text-brand-text">Neue Dokumentation</h2>
                        <p className="text-xs text-brand-text-muted mt-0.5">
                            Patient: <span className="text-brand-text">{patientName}</span>
                            <span className="mx-2">·</span>
                            Behandler: <span className="text-brand-text">{practitionerName}</span>
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-md text-brand-text-muted hover:bg-brand-surface-2 hover:text-brand-text cursor-pointer transition-colors"
                        aria-label="Schließen"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search */}
                <div className="shrink-0 px-6 py-3 border-b border-brand-border bg-brand-bg/30">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-text-muted pointer-events-none" aria-hidden />
                        <input
                            type="search"
                            autoFocus
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Vorlage suchen ..."
                            className="w-full pl-9 pr-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors"
                        />
                    </div>
                    {/* Category Chips */}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                        <button
                            type="button"
                            onClick={() => setActiveCategory('all')}
                            className={
                                activeCategory === 'all'
                                    ? 'px-2.5 py-1 rounded-md text-[11px] font-medium bg-brand-primary/10 text-brand-primary border border-brand-primary cursor-pointer transition-colors'
                                    : 'px-2.5 py-1 rounded-md text-[11px] font-medium bg-brand-surface text-brand-text-muted border border-brand-border hover:bg-brand-surface-2 hover:text-brand-text cursor-pointer transition-colors'
                            }
                        >
                            Alle ({templates.length})
                        </button>
                        {presentCategories.map(cat => {
                            const count = templates.filter(t => t.category === cat).length;
                            const active = activeCategory === cat;
                            return (
                                <button
                                    key={cat}
                                    type="button"
                                    onClick={() => setActiveCategory(cat)}
                                    className={
                                        active
                                            ? 'px-2.5 py-1 rounded-md text-[11px] font-medium bg-brand-primary/10 text-brand-primary border border-brand-primary cursor-pointer transition-colors'
                                            : 'px-2.5 py-1 rounded-md text-[11px] font-medium bg-brand-surface text-brand-text-muted border border-brand-border hover:bg-brand-surface-2 hover:text-brand-text cursor-pointer transition-colors'
                                    }
                                >
                                    {CATEGORY_LABEL[cat]} ({count})
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Template List */}
                <div className="flex-1 overflow-y-auto px-3 py-2">
                    {filtered.length === 0 ? (
                        <div className="p-8 text-center">
                            <p className="text-sm text-brand-text-muted">
                                Keine Vorlage gefunden für „{query}".
                            </p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-brand-border">
                            {filtered.map(t => (
                                <li key={t.id}>
                                    <button
                                        type="button"
                                        onClick={() => onSelect(t)}
                                        className="w-full flex items-start gap-3 px-3 py-2.5 rounded-md text-left hover:bg-brand-surface-2 cursor-pointer transition-colors group"
                                    >
                                        <FileText className="w-4 h-4 mt-0.5 text-brand-text-muted shrink-0 group-hover:text-brand-primary transition-colors" aria-hidden />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-baseline gap-2">
                                                <span className="text-sm font-medium text-brand-text truncate">
                                                    {pickLocale(t.name, locale) || t.slug}
                                                </span>
                                                <span className="text-[11px] text-brand-text-muted shrink-0">
                                                    {CATEGORY_LABEL[t.category] || t.category}
                                                </span>
                                            </div>
                                            <div className="text-[11px] text-brand-text-muted mt-0.5">
                                                {t.sections?.length || 0} Sektionen · v{t.version}
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Footer */}
                <div className="shrink-0 flex items-center justify-between px-6 py-3 border-t border-brand-border bg-brand-bg/30">
                    <label
                        className="flex items-center gap-2 text-xs text-brand-text-muted cursor-not-allowed opacity-60 select-none"
                        title="Phase B: Audio + Whisper folgt"
                    >
                        <input
                            type="checkbox"
                            disabled
                            checked={aiAssist}
                            onChange={e => setAiAssist(e.target.checked)}
                            className="rounded border-brand-border"
                        />
                        <Sparkles className="w-3.5 h-3.5" aria-hidden />
                        KI-Assistenz (folgt)
                    </label>
                    <button
                        type="button"
                        onClick={onSelectFreetext}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-brand-text-muted hover:text-brand-text cursor-pointer transition-colors"
                    >
                        <Plus className="w-3.5 h-3.5" aria-hidden />
                        Ohne Vorlage (Freitext)
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewTreatmentNoteDialog;
